import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { serviceRetryingMessage } from "../../constants/serviceUnavailableMessages";
import { useFeatureFlags, useFeatureFlagsUnavailable } from "../../hooks/useFeatureFlags";
import LoadingState from "../LoadingState";

type RequireFlagProps = {
  flag: string;
  redirectTo: string;
  children: ReactNode;
};

/**
 * Route guard that renders children only when the named flag is on.
 * While the flag list is loading, renders nothing (matches the pattern of
 * AuthenticatedAppWrapper which gates everything behind the loading state).
 * When the flag is off, redirects to the provided fallback route.
 */
export default function RequireFlag({ flag, redirectTo, children }: RequireFlagProps) {
  const { hasFlag, isLoading } = useFeatureFlags();
  const flagsUnavailable = useFeatureFlagsUnavailable();

  if (isLoading) {
    return null;
  }

  if (flagsUnavailable) {
    return <LoadingState message={serviceRetryingMessage(flagsUnavailable.service)} />;
  }

  if (!hasFlag(flag)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
